import Lenis from "lenis";
import { scrollService } from "../services.common/scrollService";
import { resizeService } from "../services.common/resizeService";

export class SiteHeader extends HTMLElement {
	public static NAME = "site-header";

	open = false;
	folded = false;
	#inertSelectors = ["main", "footer"];
	#inertElements: HTMLElement[] = [];

	connectedCallback(): void {
		this.#queryElements();
		this.#addEventListeners();
	}

	#queryElements = (): void => {
		this.#inertElements = this.#inertSelectors
			.map((selector) => document.querySelector(selector) as HTMLElement)
			.filter(Boolean);
	};

	refreshInertElements = (): void => {
		this.#queryElements();
	};

	#addEventListeners = (): void => {
		window.addEventListener("click", this.#clickCallback);
		scrollService.add(this.#scrollCallback);
		resizeService.add(this.#handleLayoutChange);
	};

	#clickCallback = (e: MouseEvent) => {
		const toggleEl = (e.target as HTMLElement).closest(
			`[${SiteHeader.NAME}-toggle]`,
		) as HTMLElement;

		if (!toggleEl) return;

		this.toggleMenu(toggleEl);
	};

	openMenu = (): void => {
		this.open = true;
		this.classList.add("open");
		this.refreshInertElements();
		this.#setTabLock();
		scrollService.disableBodyScroll();
		window.addEventListener("keydown", this.#handleKeydown);
	};

	closeMenu = (): void => {
		this.open = false;
		this.classList.remove("open");
		this.#clearTabLock();
		scrollService.enableBodyScroll();
		window.removeEventListener("keydown", this.#handleKeydown);
	};

	toggleMenu = (toggleEl?: HTMLElement): void => {
		switch (toggleEl?.dataset.action) {
			case "open":
				this.openMenu();
				break;
			case "close":
				this.closeMenu();
				break;
			default:
				(this.open ? this.closeMenu : this.openMenu)();
				break;
		}
	};

	#scrollCallback = (l?: Lenis) => {
		if (!l) return;

		if (l.animatedScroll > scrollService.lastScrollPos) {
			this.folded = true;
		}

		if (l.animatedScroll + 8 <= scrollService.lastScrollPos) {
			this.folded = false;
		}

		this.classList.toggle("folded", this.folded);
	};

	#handleLayoutChange = () => {
		if (resizeService.isLg() && this.open) {
			this.closeMenu();
		}
	};

	#handleKeydown = (e: KeyboardEvent): void => {
		switch (e.key) {
			case "Escape":
				this.closeMenu();
				break;
		}
	};

	#setTabLock = () => {
		this.#inertElements.forEach((element) => {
			element.toggleAttribute("inert", true);
		});
	};

	#clearTabLock = () => {
		this.#inertElements.forEach((element) => {
			element.toggleAttribute("inert", false);
		});
	};

	disconnectedCallback(): void {
		resizeService.remove(this.#handleLayoutChange);
		window.removeEventListener("click", this.#clickCallback);
		window.removeEventListener("keydown", this.#handleKeydown);
		scrollService.enableBodyScroll();
		this.#clearTabLock();
	}
}

customElements.define(SiteHeader.NAME, SiteHeader, { extends: "header" });
